import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLisReviewList } from '@/hooks/useLisReview'
import { resultStatusLabel } from '@/lib/lisReview'
import { formatRelativeTime } from '@/lib/relativeTime'
import { LisReviewDialog } from './LisReviewDialog'
import type { LisReviewListParams } from '@/api/lisReviewApi'

/** Everything waiting for approval in a scope, one click from each decision. */
export function LisReviewListDialog({
  open,
  onOpenChange,
  scope,
  onDecided,
  currentSubmissionId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  scope?: Pick<LisReviewListParams, 'procedureId' | 'mrn' | 'submissionId'>
  onDecided?: (outcome: 'approved' | 'rejected') => void
  currentSubmissionId?: string | null
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { data, isLoading, error } = useLisReviewList({ status: 'PENDING', ...scope }, open)
  const items = data?.items ?? []

  return (
    <>
      <Dialog open={open && !selectedId} onOpenChange={onOpenChange}>
        <DialogContent className='sm:max-w-xl'>
          <DialogHeader>
            <DialogTitle>LIS results to review</DialogTitle>
            <DialogDescription>
              Received from the laboratory and waiting for approval. Choose one to see its data.
            </DialogDescription>
          </DialogHeader>
          {isLoading ? (
            <div className='flex justify-center py-8'>
              <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
            </div>
          ) : error ? (
            <p className='py-6 text-center text-sm text-destructive'>Could not load the queue.</p>
          ) : items.length === 0 ? (
            <p className='py-6 text-center text-sm text-muted-foreground'>
              Nothing is waiting for review.
            </p>
          ) : (
            <ul className='max-h-[55vh] divide-y overflow-y-auto rounded-md border'>
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type='button'
                    onClick={() => setSelectedId(item.id)}
                    className='flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-muted/50'
                  >
                    <span className='mt-1.5 h-2 w-2 shrink-0 rounded-full bg-orange-500' />
                    <span className='flex-1'>
                      <span className='block text-sm font-medium'>
                        {item.testName ? `${item.testCode} · ${item.testName}` : item.testCode}
                      </span>
                      <span className='block text-xs text-muted-foreground'>
                        {[
                          item.mrn && `MRN ${item.mrn}`,
                          item.orderId && `Order ${item.orderId}`,
                          resultStatusLabel(item.resultStatus),
                          formatRelativeTime(item.receivedAt),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    <span className='shrink-0 text-xs text-muted-foreground'>
                      {item.proposalCount} value{item.proposalCount === 1 ? '' : 's'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
      <LisReviewDialog
        resultId={selectedId}
        onClose={() => setSelectedId(null)}
        onDecided={onDecided}
        currentSubmissionId={currentSubmissionId}
        onNavigate={() => {
          setSelectedId(null)
          onOpenChange(false)
        }}
      />
    </>
  )
}
